import {
  AllDataCompare,
  FundsNavApiResponse,
  ComparedSchemes,
  CompareSection,
  FundColumn,
  FundInput,
  FundNavSeries,
  IndexApiPoint,
  MetricRow,
  NavEntry,
  ReturnCompareData,
  CompareRiskData,
  SchemeDataItem,
  CompareApiResponse,
  CompareTableData,
  PortfolioApiItem,
  HighlightRule,
  FundWorstWithScheme,
  FundRecoveryResult,
  RecoveryGrade,
  GroupedSchemes,
  SchemeCategoryAndIsin,
  GroupedWorstReturns,
  CategoryWorstMap,
  FundDefenseScore,
  Cashflow,
  SipReturnResult,
  DatedNav,
  SipUnitsResult,
  FundCardData,
  PerformaceCategoryTabs,
  LineChartDataPerformance,
  RawPerformanceRow,
  FundProsConsResult,
} from "@/type/comparePageTypes";
import { apiPost } from "@/utils/apiClient";
import {
  calculateAge,
  calculateReturn,
  fetchSchemeProsCons,
  formatIndianNumber,
  normalizeRiskCategory,
} from "./commonFunctions";
import { FAQItem, NullableNumber, QuarterlyReturnRow, SchemeProsConsPayload } from "@/type/commonTypes";
import { OverlapSchemes } from "@/type/overlapType";
import { getWorstAndNextYearReturns } from "@/lib/prismaController";

// utils/compareUrl.ts
export function buildCompareUrl(basePath: string, currentSlug: string | null, newFundUrl: string): string {
  if (!currentSlug) {
    return `${basePath}/${newFundUrl}`;
  }

  const parts = currentSlug.split("-vs-");

  if (parts.includes(newFundUrl)) {
    return `${basePath}/${currentSlug}`; // prevent duplicate
  }

  if (parts.length >= 4) {
    return `${basePath}/${currentSlug}`; // max 4
  }

  return `${basePath}/${currentSlug}-vs-${newFundUrl}`;
}

// ----------- For Nav Data ----------------
export async function fetchFundsNavData(funds: FundInput[]): Promise<FundNavSeries[] | null> {
  const COLORS = ["#0EA5A4", "#2563EB", "#7C3AED", "#F97316"];

  const results = funds.map((fund, index) => {
    return {
      name: fund.scheme_name,
      color: COLORS[index % COLORS.length], // assign colors in order
      data: [], // will fill later
    };
  });

  // Fetch NAV data for all funds
  let navData: FundsNavApiResponse[] = [];

  try {
    navData = await Promise.all(
      funds.map((fund) =>
        apiPost<FundsNavApiResponse>("data/navDetailsWeb", {
          amc_code: fund.amc_code,
          kra: fund.kra,
          scheme_code: fund.scheme_code,
          scheme_name: fund.scheme_name,
          url: fund.url,
        }),
      ),
    );
  } catch (error) {
    console.error(`${error} :- something went wrong`);
    return null;
  }

  // Combine NAV data with color assignment
  return results.map((fundResult, i) => ({
    ...fundResult,
    data: navData[i].navData,
  }));
}

// ----------- For Charts Date Function ----------------
type RangeKey = "1M" | "3M" | "6M" | "YTD" | "1Y" | "3Y" | "5Y" | "ALL";

export function normalizeNavMap(input: Record<string, NavEntry>): NavEntry[] {
  return Object.values(input);
}

export function normalizeIndexData(data: IndexApiPoint[]): [number, number][] {
  return data.map((d) => [new Date(d.date).getTime(), d.close] as [number, number]).sort((a, b) => a[0] - b[0]);
}

export function toTime(value: Date | number | string): number {
  return value instanceof Date ? value.getTime() : new Date(value).getTime();
}

export function getCutoffDate(latest: Date, range: RangeKey): Date | null {
  const date = new Date(latest);

  switch (range) {
    case "1M":
      date.setMonth(date.getMonth() - 1);
      return date;

    case "3M":
      date.setMonth(date.getMonth() - 3);
      return date;

    case "6M":
      date.setMonth(date.getMonth() - 6);
      return date;

    case "1Y":
      date.setFullYear(date.getFullYear() - 1);
      return date;

    case "3Y":
      date.setFullYear(date.getFullYear() - 3);
      return date;

    case "5Y":
      date.setFullYear(date.getFullYear() - 5);
      return date;

    case "YTD":
      return new Date(latest.getFullYear(), 0, 1);

    case "ALL":
      return null;
  }
}

export function rangeFromExtremes(min: number, max: number): RangeKey {
  const start = new Date(min);
  const end = new Date(max);

  // 🔹 YTD detection first (must be first)
  if (
    start.getFullYear() === end.getFullYear() &&
    start.getMonth() === 0 && // January
    start.getDate() === 1
  ) {
    return "YTD";
  }

  const days = Math.round((max - min) / 86400000);

  if (days <= 31) return "1M";
  if (days <= 92) return "3M";
  if (days <= 183) return "6M";
  if (days <= 366) return "1Y";
  if (days <= 366 * 3) return "3Y";
  if (days <= 366 * 5) return "5Y";

  return "ALL";
}

// ----------- Returns Calculation ------------
export const calculateReturnsForChart = (chart: Highcharts.Chart, setReturns: React.Dispatch<React.SetStateAction<Record<string, number>>>): void => {
  if (!chart.xAxis || chart.xAxis.length <= 0) return;
  const xAxis = chart.xAxis[0];
  const { min, max } = xAxis.getExtremes();

  if (typeof min !== "number" || typeof max !== "number") return;

  const nextReturns: Record<string, number> = {};

  chart.series.forEach((s) => {
    if (!s.visible || !Array.isArray(s.points)) return;

    const value = calculateReturn(s.points, min, max);
    nextReturns[s.name] = value;
  });

  setReturns(nextReturns);
};

// ----------- For table ------------
const rupee = (v: string | number): string => `₹ ${v}`;
const percent = (v?: string | number | null, dacimalVal?: number): string => {
  if (v == null || v == undefined || v === 0) return "-";
  return `${Number(v).toFixed(dacimalVal ?? 2)}%`;
};

const valueTrue = (v?: string | number | null): string => {
  if (v == null || v == undefined || v == 0) return "-";
  return String(v);
};

const fundLockIn = (v?: string | number | null): string => {
  if (v == null || v == undefined || v == 0) return "-";
  const val = v === "52" || v === "64" ? "~5 Yr" : v === "20" ? "~3 Yr" : "No Lock-in";
  return String(val);
};

function getHighlightIndex(values: number[], rule?: HighlightRule): number | undefined {
  if (!rule || values.length === 0) return undefined;

  const filtered = rule.onlyIfPositive ? values.map((v) => (v > 0 ? v : Number.NEGATIVE_INFINITY)) : values;

  if (rule.mode === "max") {
    const max = Math.max(...filtered);
    return max > 0 ? filtered.indexOf(max) : undefined;
  }

  const min = Math.min(...filtered);
  return min >= 0 ? filtered.indexOf(min) : undefined;
}

// Table Columns
export function buildColumns(compared: ComparedSchemes): FundColumn[] {
  return compared.scheme_name.value.map((name, i) => ({
    id: `fund-${i}`,
    name,
  }));
}

// Basic Overview
export function buildBasicOverview(all: AllDataCompare, schemeData: SchemeDataItem[]): CompareSection {
  return {
    id: "basic-overview",
    title: "Basic Overview",
    rows: [
      {
        id: "nav",
        label: "Current NAV",
        values: schemeData.map((s) => rupee(Number(s.latest_nav).toFixed(2) ?? "-")),
      },
      {
        id: "benchmark",
        label: "Benchmark",
        values: all.benchmark.value.map(valueTrue),
      },
      {
        id: "aum",
        label: "Fund Size (AUM)",
        values: all.asset_size_cr.value.map(valueTrue),
      },
      {
        id: "expense",
        label: "Expense Ratio",
        values: all.expense_ratio.value.map(percent),
        highlightIndex: getHighlightIndex(
          all.expense_ratio.value.map((v) => Number(v)),
          {
            mode: "min",
            onlyIfPositive: true,
          },
        ),
      },
      {
        id: "launch",
        label: "Launch Date",
        values: all.launch_date.value.map(valueTrue),
      },
      {
        id: "fund-managers",
        label: "Fund Managers",
        values: schemeData.map((s) => s.fund_manager.split(",")[0].trim() ?? "-"),
      },
      {
        id: "turnover",
        label: "Turnover",
        values: schemeData.map((s) => percent(Number(s.scheme_turnover) ?? "-", 0)),
      },
    ],
    descriptionKey: "basic-overview-compare",
  };
}

// SIP Returns
export function buildSIPReturns(data: ReturnCompareData): CompareSection {
  const row = (id: string, label: string, values: number[]): MetricRow => ({
    id,
    label,
    values: values.map(percent),
    highlightIndex: getHighlightIndex(values, {
      mode: "max",
      onlyIfPositive: true,
    }),
  });

  return {
    id: "sip_returns",
    title: "SIP Returns",
    rows: [row("1y", "1 Year Return", data.one_year_abs.value.sip_return), row("3y", "3 Year Return", data.three_year.value.sip_return), row("5y", "5 Year Return", data.five_year.value.sip_return), row("since", "Since Inception", data.five_year.value.rolling_return)],
    descriptionKey: "sip-returns",
  };
}

// Trailing Returns
export function buildTrailingReturns(data: ReturnCompareData): CompareSection {
  const row = (id: string, label: string, values: number[]): MetricRow => ({
    id,
    label,
    values: values.map(percent),
    highlightIndex: getHighlightIndex(values, {
      mode: "max",
      onlyIfPositive: true,
    }),
  });

  return {
    id: "trailing_returns",
    title: "Trailing Returns",
    rows: [row("1M", "1 Month Return", data.one_month_abs.value.trailing_return), row("6M", "3 Month Return", data.six_month_abs.value.trailing_return), row("1y", "1 Year Return", data.one_year_abs.value.trailing_return), row("3y", "3 Year Return", data.three_year.value.trailing_return), row("5y", "5 Year Return", data.five_year.value.trailing_return)],
    descriptionKey: "trailing-returns",
  };
}

// Rolling Returns
export function buildRollingReturns(data: ReturnCompareData, isLoggedIn: boolean): CompareSection {
  const row = (id: string, label: string, values: number[] | string[]): MetricRow => ({
    id,
    label,
    values: isLoggedIn ? values.map(percent) : values.map(() => "00"),
    highlightIndex: getHighlightIndex(isLoggedIn ? values.map((v) => Number(v)) : values.map(() => 0), {
      mode: "max",
      onlyIfPositive: true,
    }),
    isLocked: !isLoggedIn,
  });

  return {
    id: "rolling_returns",
    title: "Rolling Returns",
    rows: [row("1y", "1 Year Return", data.one_year_abs.value.rolling_return), row("3y", "3 Year Return", data.three_year.value.rolling_return), row("5y", "5 Year Return", data.five_year.value.rolling_return)],
    descriptionKey: "rolling-return",
  };
}

// Risk Matrics
export function buildRiskMetrics(risk: CompareRiskData): CompareSection {
  return {
    id: "risk",
    title: "Risk Metrics (Standard)",
    rows: [
      {
        id: "alpha",
        label: "Alpha",
        values: risk.Alpha.value.fund.map(valueTrue),
        highlightIndex: getHighlightIndex(
          risk.Alpha.value.fund.map((v) => Number(v)),
          {
            mode: "max",
            onlyIfPositive: true,
          },
        ),
      },
      {
        id: "beta",
        label: "Beta",
        values: risk.Beta.value.fund.map(valueTrue),
        highlightIndex: getHighlightIndex(
          risk.Beta.value.fund.map((v) => Number(v)),
          {
            mode: "min",
            onlyIfPositive: true,
          },
        ),
      },
      {
        id: "sharpe",
        label: "Sharpe Ratio",
        values: risk.Sharp.value.fund.map(valueTrue),
        highlightIndex: getHighlightIndex(
          risk.Sharp.value.fund.map((v) => Number(v)),
          {
            mode: "max",
            onlyIfPositive: true,
          },
        ),
      },
      {
        id: "std",
        label: "Standard Deviation",
        values: risk["Standard Deviation"].value.fund.map(valueTrue),
        highlightIndex: getHighlightIndex(
          risk["Standard Deviation"].value.fund.map((v) => Number(v)),
          {
            mode: "min",
            onlyIfPositive: true,
          },
        ),
      },
    ],
    descriptionKey: "risk-comparison",
  };
}

// Investment Information
export function buildInvestmentAndTaxes(all: AllDataCompare, schemeData: SchemeDataItem[]): CompareSection {
  return {
    id: "investment",
    title: "Investment & Taxes",
    rows: [
      {
        id: "min-sip",
        label: "Min SIP Amount",
        values: schemeData.map((s) => `₹ ${formatIndianNumber(s.minimum_sip_invest)}`),
      },
      {
        id: "min-lump",
        label: "Min Lumpsum",
        values: schemeData.map((s) => `₹ ${formatIndianNumber(s.minimum_investment)}`),
      },
      {
        id: "exit-load",
        label: "Exit Load",
        values: all.exit_load.value.map(valueTrue),
      },
      {
        id: "lock_in",
        label: "Lock In",
        values: schemeData.map((v) => fundLockIn(v.scheme_category_id)),
      },
    ],
    descriptionKey: "funds-information",
  };
}

// Portfolio Market Cap
// export function buildPortfolioMarketCap(
//   schemeData: SchemeDataItem[],
//   portfolioData: PortfolioApiItem[],
// ): CompareSection {
//   const fundsWithPortfolios = new Map(
//     portfolioData.map((item) => [item.schemeCode, item]),
//   );

//   const alignedData = schemeData.map(
//     (scheme) => fundsWithPortfolios.get(scheme.scheme_code) ?? null,
//   );

//   const allocationNames = Array.from(
//     new Set(
//       alignedData.flatMap((item) =>
//         item?.data?.resultArray
//           ? Object.values(item.data.resultArray).flatMap((block) =>
//               block?.market_cap
//                 ? block.market_cap.map((a) => a.instrument)
//                 : [],
//             )
//           : [],
//       ),
//     ),
//   );

//   const allocationRows = allocationNames.map((name) => {
//     const numericValues = alignedData.map((item) => {
//       if (!item?.data?.resultArray) return null;

//       for (const block of Object.values(item.data.resultArray)) {
//         const found = block?.market_cap?.find((a) => a.instrument === name);
//         if (found) return found.holdings;
//       }

//       return null;
//     });

//     return {
//       id: name.toLowerCase().replace(/\s+/g, "-"),
//       label: name,
//       values: numericValues.map((v) =>
//         typeof v === "number" ? percent(v) : "-",
//       ),
//     };
//   });

//   const turnoverValues = schemeData.map((s) => s.scheme_turnover ?? null);

//   return {
//     id: "portfolio-market-cap",
//     title: "Portfolio Market Cap",
//     rows: [
//       ...allocationRows,
//       {
//         id: "turnover",
//         label: "Turnover Ratio",
//         values: turnoverValues.map((v) => percent(v)),
//       },
//     ],
//     descriptionKey: "market-cap-equity",
//   };
// }

export function buildAssetAllocation(schemeData: SchemeDataItem[], portfolioData: PortfolioApiItem[]): CompareSection {
  const portfolioMap = new Map<string, PortfolioApiItem>(portfolioData.map((item) => [item.schemeCode, item]));

  const alignedData: (PortfolioApiItem | null)[] = schemeData.map((scheme) => portfolioMap.get(scheme.scheme_code) ?? null);

  const allocationMap = new Map<string, (number | null)[]>();

  alignedData.forEach((item, schemeIndex) => {
    const tabList = item?.data?.tabList ?? [];

    tabList.forEach((tab) => {
      if (!allocationMap.has(tab.name)) {
        allocationMap.set(tab.name, Array(alignedData.length).fill(null));
      }

      const current = allocationMap.get(tab.name);
      if (!current) return;

      current[schemeIndex] = tab.allocation ?? null;
    });
  });

  const rows: MetricRow[] = Array.from(allocationMap.entries())
    .map(([name, values]) => {
      const hasData = values.some((v) => typeof v === "number" && v > 0);
      if (!hasData) return null;

      return {
        id: name.toLowerCase().replace(/\s+/g, "-"),
        label: name,
        values: values.map((v) => (typeof v === "number" ? percent(v) : "-")),
      };
    })
    .filter((row): row is MetricRow => row !== null)
    .sort((a, b) => {
      const aRank = a.id === "others" ? 1 : 0;
      const bRank = b.id === "others" ? 1 : 0;
      return aRank - bRank;
    });

  return {
    id: "asset-allocation",
    title: "Asset Allocation",
    rows,
  };
}

const MARKETCAPS = ["Small Cap", "Large Cap", "Mid Cap", "Micro Cap", "Flexi Cap"];
const OTHERS = "Others";

export function buildPortfolioMarketCap(schemeData: SchemeDataItem[], portfolioData: PortfolioApiItem[]): CompareSection {
  const fundsWithPortfolios = new Map(portfolioData.map((item) => [item.schemeCode, item]));

  const alignedData = schemeData.map((scheme) => fundsWithPortfolios.get(scheme.scheme_code) ?? null);

  const allocationNames = [...MARKETCAPS, OTHERS];

  const allocationRows = allocationNames
    .map((name) => {
      const numericValues = alignedData.map((item) => {
        if (!item?.data?.resultArray) return null;

        let othersTotal = 0;
        let value: number | null = null;

        for (const block of Object.values(item.data.resultArray)) {
          const caps = block?.market_cap ?? [];

          for (const cap of caps) {
            if (MARKETCAPS.includes(cap.instrument)) {
              if (cap.instrument === name) {
                value = cap.holdings ?? 0;
              }
            } else {
              if (name === OTHERS) {
                othersTotal += cap.holdings ?? 0;
              }
            }
          }
        }

        if (name === OTHERS) {
          return othersTotal > 0 ? othersTotal : null;
        }

        return value;
      });

      // remove row if completely empty
      const hasData = numericValues.some((v) => typeof v === "number" && v > 0);

      if (!hasData) return null;

      return {
        id: name.toLowerCase().replace(/\s+/g, "-"),
        label: name,
        values: numericValues.map((v) => (typeof v === "number" ? percent(v) : "-")),
      };
    })
    .filter((row): row is MetricRow => row !== null);

  return {
    id: "portfolio-market-cap",
    title: "Portfolio Market Cap",
    rows: allocationRows,
    descriptionKey: "market-cap-equity",
  };
}
// Portfolio Sectors
export function buildPortfolioSectors(schemeData: SchemeDataItem[], portfolioData: PortfolioApiItem[]): CompareSection {
  const fundsWithPortfolios = new Map(portfolioData.map((item) => [item.schemeCode, item]));

  const alignedData = schemeData.map((scheme) => fundsWithPortfolios.get(scheme.scheme_code) ?? null);

  const sectorNames = Array.from(new Set(alignedData.flatMap((item) => (item?.data?.resultArray ? Object.values(item.data.resultArray).flatMap((block) => (block?.sector ? block.sector.map((a) => a.instrument) : [])) : []))));

  const sectorRows = sectorNames.map((name) => {
    const numericValues = alignedData.map((item) => {
      if (!item?.data?.resultArray) return null;

      for (const block of Object.values(item.data.resultArray)) {
        const found = block?.sector?.find((a) => a.instrument === name);
        if (found) return found.holdings;
      }

      return null;
    });

    return {
      id: name.toLowerCase().replace(/\s+/g, "-"),
      label: name,
      values: numericValues.map((v) => (typeof v === "number" ? percent(v) : "-")),
    };
  });

  return {
    id: "portfolio-sectors",
    title: "Portfolio Sectors",
    rows: sectorRows,
    descriptionKey: "portfolio-sectors",
  };
}

// All functions Combine rows
export function buildCompareTableData(api: CompareApiResponse, portfolioData: PortfolioApiItem[], isLoggedIn: boolean): CompareTableData {
  return {
    columns: buildColumns(api.compared_schemes),
    sections: [
      buildBasicOverview(api.all_data, api.schemeData), // Basic Overview
      buildSIPReturns(api.return_data), // SIP Returns
      buildTrailingReturns(api.return_data), // Trailing Returns
      buildRollingReturns(api.return_data, isLoggedIn), // Rolling Returns
      buildRiskMetrics(api.riskData), // Risk Matrics
      buildInvestmentAndTaxes(api.all_data, api.schemeData), // Investment Information
      buildAssetAllocation(api.schemeData, portfolioData), // Portfolio Allocations
      buildPortfolioMarketCap(api.schemeData, portfolioData), // Portfolio Market Cap
      buildPortfolioSectors(api.schemeData, portfolioData), // Portfolio Sectors
    ],
  };
}

// ----------- Worst Returns Calculation ------------
export function mapRecoveryGrade(funds: FundWorstWithScheme[]): FundRecoveryResult[] {
  const count = funds.length;

  return funds.map((item, index): FundRecoveryResult => {
    const worst = item.worstReturn;
    const next = item.nextYearReturn;
    const diff = next - worst;

    let recovery: RecoveryGrade;

    // --- Single fund logic ---
    if (count === 1) {
      if (worst < 0 && next < 0) {
        recovery = "slow";
      } else if (diff >= 25) {
        recovery = "excellent";
      } else {
        recovery = "fast";
      }
    }
    // --- Two funds logic ---
    else if (count === 2) {
      recovery = index === 0 ? "fast" : "slow";
    }
    // --- Three or more funds ---
    else {
      if (index === 0) recovery = "excellent";
      else if (index === 1) recovery = "fast";
      else recovery = "slow";
    }

    // --- Safety downgrade ---
    if (next <= 0) {
      recovery = "slow";
    }

    return {
      ...item,
      recovery,
    };
  });
}

// ----------- Grouped By Scheme Category ------------
export function groupBySchemeCategory(data: SchemeCategoryAndIsin[]): GroupedSchemes {
  return data.reduce<GroupedSchemes>((acc, item) => {
    const { scheme_category_id, u_code } = item;

    // ⛔ skip invalid rows
    if (!scheme_category_id || !u_code) return acc;

    if (!acc[scheme_category_id]) {
      acc[scheme_category_id] = {
        scheme_category_id,
        u_codes: [],
      };
    }

    if (!acc[scheme_category_id].u_codes.includes(u_code)) {
      acc[scheme_category_id].u_codes.push(u_code);
    }

    return acc;
  }, {});
}

export async function getWorstAndNextYearReturnsGrouped(groupedSchemes: GroupedSchemes): Promise<GroupedWorstReturns> {
  const entries = Object.entries(groupedSchemes);

  const results = await Promise.all(
    entries.map(async ([schemeCategory, group]) => {
      const rows = await getWorstAndNextYearReturns(group.u_codes);

      return {
        schemeCategory,
        rows,
      };
    }),
  );

  return results.reduce<GroupedWorstReturns>((acc, item) => {
    acc[item.schemeCategory] = item.rows;
    return acc;
  }, {});
}

// ----------- Calculate the Defense Score ------------
export function buildWorstYearDefenseRanking(worstReturns: QuarterlyReturnRow[], funds: OverlapSchemes[], isLoggedIn: boolean): FundRecoveryResult[] {
  // 1. Build fund lookup
  const fundMap = new Map<string, OverlapSchemes>();
  for (const fund of funds) {
    fundMap.set(fund.u_code, fund);
  }

  // 2. Group quarterly returns by ISIN
  const grouped = new Map<string, QuarterlyReturnRow[]>();
  for (const row of worstReturns) {
    const list = grouped.get(row.isin) ?? [];
    list.push(row);
    grouped.set(row.isin, list);
  }

  // 3. Extract worst / next year per fund
  const worstFunds: FundWorstWithScheme[] = [];

  for (const [isin, rows] of grouped) {
    if (rows.length !== 2) continue;

    const [a, b] = rows.sort((x, y) => x.quarter_year - y.quarter_year);
    const worst = a.return_percentage <= b.return_percentage ? a : b;
    const next = worst === a ? b : a;

    const fund = fundMap.get(isin);
    if (!fund) continue;

    worstFunds.push({
      isin,
      worstYear: isLoggedIn ? worst.quarter_year : 0,
      worstReturn: isLoggedIn ? worst.return_percentage : 0,
      nextYear: isLoggedIn ? next.quarter_year : 0,
      nextYearReturn: isLoggedIn ? next.return_percentage : 0,
      fund,
    });
  }

  // 4. Sort by defensive strength
  worstFunds.sort((x, y) => {
    if (x.worstReturn !== y.worstReturn) {
      return y.worstReturn - x.worstReturn;
    }
    return y.nextYearReturn - x.nextYearReturn;
  });

  // 5. Apply recovery grades
  const withRecovery = mapRecoveryGrade(worstFunds);

  return withRecovery;
}

export function buildCategoryWorstReturnMap(groupedWorstReturns: GroupedWorstReturns): CategoryWorstMap {
  return Object.entries(groupedWorstReturns).reduce<CategoryWorstMap>((acc, [category, rows]) => {
    const byIsin = new Map<string, number>();

    for (const row of rows) {
      const existing = byIsin.get(row.isin);
      if (existing === undefined || row.return_percentage < existing) {
        byIsin.set(row.isin, row.return_percentage);
      }
    }

    acc[category] = Array.from(byIsin.entries()).map(([isin, worstReturn]) => ({
      isin,
      worstReturn,
    }));

    return acc;
  }, {});
}

export function scoreFundsAgainstCategory(rankedFunds: FundRecoveryResult[], categoryWorstMap: CategoryWorstMap, isLoggedIn: boolean): FundDefenseScore[] {
  return rankedFunds.map((fund) => {
    const category = fund.fund.scheme_category_id;
    const categoryData = categoryWorstMap[category];

    if (!categoryData || categoryData.length === 0 || !isLoggedIn) {
      return {
        ...fund,
        defenseScore: 0,
      };
    }

    const returns = categoryData.map((r) => r.worstReturn);

    const best = Math.max(...returns);
    const worst = Math.min(...returns);

    let score = 0;

    if (best !== worst) {
      score = ((fund.worstReturn - worst) / (best - worst)) * 100;
    }

    return {
      ...fund,
      defenseScore: Math.round(Math.min(100, Math.max(0, score))),
    };
  });
}

export function buildWorstYearDefenseScores(params: { worstReturns: QuarterlyReturnRow[]; funds: OverlapSchemes[]; otherFunds: GroupedWorstReturns; isLoggedIn: boolean }): FundDefenseScore[] {
  const { worstReturns, funds, otherFunds, isLoggedIn } = params;

  const rankedFunds = buildWorstYearDefenseRanking(worstReturns, funds, isLoggedIn);

  const categoryWorstMap = buildCategoryWorstReturnMap(otherFunds);

  return scoreFundsAgainstCategory(rankedFunds, categoryWorstMap, isLoggedIn);
}

// ----------- Calculate SIP By Nav Of Funds ------------
function toDate(value: Date | string): Date {
  return value instanceof Date ? value : new Date(value);
}

// Set the NAV for Tenure
function getNavsWithinTenure(navMap: Record<string, NavEntry>, tenureYears: number): { date: Date; nav: number }[] {
  const cutoff = new Date();
  cutoff.setFullYear(cutoff.getFullYear() - tenureYears);

  return Object.values(navMap)
    .map((n) => ({
      date: toDate(n.date),
      nav: Number(n.nav),
    }))
    .filter((n) => n.date >= cutoff)
    .sort((a, b) => a.date.getTime() - b.date.getTime());
}

function getLatestNav(navs: DatedNav[]): DatedNav | null {
  if (navs.length === 0) return null;
  return navs[navs.length - 1];
}

function getNavOnOrAfterDate(navs: DatedNav[], targetDate: Date): DatedNav | null {
  for (const nav of navs) {
    if (nav.date >= targetDate) {
      return nav;
    }
  }
  return null;
}

export function calculateSipUnits(navs: { date: Date; nav: number }[], monthlyInvestment: number, tenureYears: number, sipDay: number = 5): SipUnitsResult {
  const sortedNavs = [...navs].sort((a, b) => a.date.getTime() - b.date.getTime());
  const totalMonths = tenureYears * 12;

  const monthlyUnits: SipUnitsResult["monthlyUnits"] = [];

  let totalUnits = 0;
  let investedAmount = 0;

  const today = new Date();
  const startDate = new Date(today);
  startDate.setFullYear(today.getFullYear() - tenureYears);

  for (let i = 0; i < totalMonths; i++) {
    const sipDate = new Date(startDate.getFullYear(), startDate.getMonth() + i, sipDay);

    const navEntry = getNavOnOrAfterDate(sortedNavs, sipDate);
    if (!navEntry) continue;

    const units = monthlyInvestment / navEntry.nav;

    monthlyUnits.push({
      sipDate,
      navDate: navEntry.date,
      nav: navEntry.nav,
      units,
    });

    totalUnits += units;
    investedAmount += monthlyInvestment;
  }

  const latestNavEntry = getLatestNav(sortedNavs);
  const currentValue = latestNavEntry ? totalUnits * latestNavEntry.nav : 0;

  const gainLoss = currentValue - investedAmount;
  const gainLossPercent = investedAmount > 0 ? (gainLoss / investedAmount) * 100 : 0;

  return {
    monthlyUnits,
    totalUnits,
    investedAmount,
    currentValue,
    gainLoss,
    gainLossPercent,
  };
}

// Calculating the XIRR
function calculateXirr(cashflows: Cashflow[]): number | null {
  let rate = 0.1; // initial guess 10%

  for (let i = 0; i < 100; i++) {
    let npv = 0;
    let dNpv = 0;
    const baseDate = cashflows[0].date;

    for (const cf of cashflows) {
      const years = (cf.date.getTime() - baseDate.getTime()) / (365 * 24 * 60 * 60 * 1000);

      const factor = Math.pow(1 + rate, years);
      npv += cf.amount / factor;
      dNpv -= (years * cf.amount) / (factor * (1 + rate));
    }

    const newRate = rate - npv / dNpv;
    if (Math.abs(newRate - rate) < 1e-6) return newRate;

    rate = newRate;
  }

  return null;
}

export function calculateSipReturnsForFunds({ fundsNav, monthlyInvestment, tenureYears }: { fundsNav: FundNavSeries[]; monthlyInvestment: number; tenureYears: number }): SipReturnResult[] {
  const today = new Date();

  return fundsNav.map((fund) => {
    const navs = getNavsWithinTenure(fund.data, tenureYears);
    const navDates = Object.keys(fund.data);

    const firstNavDate = navDates.length > 0 ? new Date(navDates[0]) : null;

    const fundAgeInYears = firstNavDate !== null ? calculateAge(firstNavDate, today) : null;

    if (navs.length === 0) {
      return {
        fundName: fund.name,
        investedAmount: 0,
        currentValue: 0,
        gainLoss: 0,
        gainLossPercent: 0,
        annualizedReturn: null,
        fundAgeInYears: null,
      };
    }

    const sipResult = calculateSipUnits(navs, monthlyInvestment, tenureYears);

    const { monthlyUnits, investedAmount, currentValue, gainLoss, gainLossPercent } = sipResult;

    const cashflows: Cashflow[] = monthlyUnits.map((m) => ({
      amount: -monthlyInvestment,
      date: m.sipDate,
    }));

    cashflows.push({
      amount: currentValue,
      date: today,
    });

    const xirr = calculateXirr(cashflows);

    return {
      fundName: fund.name,
      investedAmount,
      currentValue,
      gainLoss,
      gainLossPercent,
      annualizedReturn: xirr ? xirr * 100 : null,
      fundAgeInYears,
    };
  });
}

function isValidReturn(value: NullableNumber): value is number {
  return value !== null && value !== undefined && value !== 0;
}

function pickReturnByPriority(index: number, sources: { label: string; values: NullableNumber[] }[]): { value: number; label: string } {
  for (const source of sources) {
    const v = source.values[index];
    if (isValidReturn(v)) {
      return { value: v, label: source.label };
    }
  }

  return { value: 0, label: sources[sources.length - 1].label };
}

export function matchOrderByIds<T extends { id: string | number }>(expectedOrder: readonly number[], data: readonly T[]): T[] {
  // Fast lookup map
  const map = new Map<number, T>();

  for (const item of data) {
    map.set(Number(item.id), item);
  }

  // Build ordered array
  const ordered: T[] = [];

  for (const id of expectedOrder) {
    const found = map.get(id);

    if (found) {
      ordered.push(found);
    }
  }

  if (ordered.length === data.length) {
    let sameOrder = true;

    for (let i = 0; i < data.length; i++) {
      if (Number(data[i].id) !== expectedOrder[i]) {
        sameOrder = false;
        break;
      }
    }

    if (sameOrder) {
      return [...data]; // already in correct order
    }
  }

  return ordered;
}

// ----------- all same time period returns -----------
function buildFooterTextFromList(list: string[] | undefined, fallback: string, maxItems: number): string {
  if (!list || list.length === 0) return fallback;

  return list.slice(0, maxItems).join(" ");
}

function mapProsConsBySchemeCode(data: FundProsConsResult[]): Record<string, FundProsConsResult> {
  return data.reduce<Record<string, FundProsConsResult>>((acc, item) => {
    acc[item.scheme_code] = item;
    return acc;
  }, {});
}

export function buildFundCards(api: CompareApiResponse, prosAndConsData: FundProsConsResult[], login: boolean): FundCardData[] {
  const schemes = api.schemeData;

  const prosConsMap = mapProsConsBySchemeCode(prosAndConsData);

  const sources = [
    { label: "5Y", values: api.return_data.five_year.value.trailing_return },
    { label: "3Y", values: api.return_data.three_year.value.trailing_return },
    { label: "1Y", values: api.return_data.one_year_abs.value.trailing_return },
    {
      label: "6M",
      values: api.return_data.six_month_abs.value.trailing_return,
    },
    {
      label: "1M",
      values: api.return_data.one_month_abs.value.trailing_return,
    },
  ];

  const avgReturns = schemes.map((_, index) => {
    const values: number[] = [];

    const r1 = api.return_data.one_year_abs.value.rolling_return[index];
    const r3 = api.return_data.three_year.value.rolling_return[index];
    const r5 = api.return_data.five_year.value.rolling_return[index];

    if (isValidReturn(r1)) values.push(r1);
    if (isValidReturn(r3)) values.push(r3);
    if (isValidReturn(r5)) values.push(r5);

    if (values.length === 0) return 0;

    return values.reduce((a, b) => a + b, 0) / values.length;
  });

  const bestIndex = avgReturns.reduce((best, current, index, arr) => (current > arr[best] ? index : best), 0);

  return schemes.map((scheme, index): FundCardData => {
    const { value: returnsShown, label } = pickReturnByPriority(index, sources);

    const isBestPick = !login || schemes.length < 2 ? false : index === bestIndex;
    const prosCons = prosConsMap[scheme.scheme_code];

    const bestPickFooter = buildFooterTextFromList(prosCons?.pros, "Best average returns across 1, 3 and 5 years among compared funds.", 2);

    const tradeOffFooter = buildFooterTextFromList(prosCons?.cons, "Good fund, but overall performance is lower than the top performer.", 2);
    return {
      id: String(scheme.id),
      scheme_name: scheme.scheme_name,
      scheme_code: scheme.scheme_code,
      sip_allowed: scheme.sip_allowed,
      lump_allowed: scheme.lump_allowed,
      minimum_sip_invest: scheme.minimum_sip_invest,
      minimum_investment: scheme.minimum_investment,
      amc_code: scheme.amc_code,
      url: scheme.url,
      returnLabel: label,
      returns: returnsShown,
      expenseRatio: Number(scheme.expense_ratio),
      footerTitle: isBestPick ? "Why it's the winner:" : "The trade-off:",
      footerText: isBestPick ? bestPickFooter : tradeOffFooter,
      isBestPick,
    };
  });
}

export const transformDataPerformanceData = (raw: RawPerformanceRow[], type: PerformaceCategoryTabs): LineChartDataPerformance[] => {
  const monthNames = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

  const currentDate = new Date();
  const currentYear = currentDate.getFullYear();
  const currentMonthIndex = currentDate.getMonth();

  if (type === "monthly") {
    const months: LineChartDataPerformance[] = [];

    let year = currentYear;
    let monthIndex = currentMonthIndex;

    for (let i = 0; i < 8; i++) {
      const yearData = raw.find((d) => d.return_year === year);

      if (yearData) {
        const key = monthNames[monthIndex].toLowerCase() + "_return";

        months.unshift({
          year: `${monthNames[monthIndex]} ${year}`,
          value: Number(yearData[key as keyof RawPerformanceRow]) || 0,
        });
      }

      monthIndex--;
      if (monthIndex < 0) {
        monthIndex = 11;
        year--;
      }
    }

    return months;
  }

  if (type === "yearly") {
    return raw
      .map((d) => ({
        year: d.return_year.toString(),
        value: Number(d.monthly_return_percentage) || 0,
      }))
      .sort((a, b) => Number(a.year) - Number(b.year))
      .slice(-8);
  }

  if (type === "quarterly") {
    const labels = ["Q1", "Q2", "Q3", "Q4"];
    const quarters: LineChartDataPerformance[] = [];

    const currentQuarter = Math.floor(currentMonthIndex / 3) + 1;

    let year = currentYear;
    let q = currentQuarter;

    for (let i = 0; i < 8; i++) {
      const yearData = raw.find((d) => d.return_year === year);

      if (yearData) {
        const key = `quarter_${q}_return` as keyof RawPerformanceRow;

        quarters.unshift({
          year: `${labels[q - 1]} ${year}`,
          value: Number(yearData[key]) || 0,
        });
      }

      q--;
      if (q < 1) {
        q = 4;
        year--;
      }
    }

    return quarters;
  }

  return [];
};

// ----------- for FAQs ------------
const riskRank: Record<string, number> = {
  "very low": 1,
  low: 2,
  medium: 3,
  "moderate to high": 4,
  high: 5,
  "very high": 6,
};

function compareRisk(a: string, b: string): string {
  const rankA = riskRank[a.toLowerCase()] ?? 0;
  const rankB = riskRank[b.toLowerCase()] ?? 0;

  if (rankA === rankB) return "equally volatile";
  if (rankA > rankB) return "more volatile";

  return "less volatile";
}

function getFiveYearBenchmark(value: string): number | null {
  try {
    const parsed = JSON.parse(value) as { five_year_return: number };
    return parsed.five_year_return ?? null;
  } catch {
    return null;
  }
}

export function buildDynamicFaq(fundsDataBySlug: OverlapSchemes[]): FAQItem[] {
  if (fundsDataBySlug.length < 2) return [];

  const [fundA, fundB] = fundsDataBySlug;

  const fundAName = fundA.scheme_name;
  const fundBName = fundB.scheme_name;

  const fundABenchmark5Y = getFiveYearBenchmark(fundA.benchmark_return);
  const fundBBenchmark5Y = getFiveYearBenchmark(fundB.benchmark_return);

  const lowerExpenseFund = fundA.expense_ratio <= fundB.expense_ratio ? fundA : fundB;

  const betterBenchmarkFund = (fundABenchmark5Y ?? 0) >= (fundBBenchmark5Y ?? 0) ? fundA : fundB;

  const riskComparison = compareRisk(normalizeRiskCategory(fundA.scheme_risk), normalizeRiskCategory(fundB.scheme_risk));

  const hasValidReturns = typeof fundA.sr_five_year === "number" && typeof fundB.sr_five_year === "number" && fundA.sr_five_year > 0 && fundB.sr_five_year > 0;

  const faqData: FAQItem[] = [];

  if (hasValidReturns) {
    faqData.push({
      question: `Which fund has higher returns, ${fundAName} or ${fundBName}?`,
      answer: `${fundAName} had a 5-year average annual growth rate of ${fundA.sr_five_year}%, while ${fundBName}'s was ${fundB.sr_five_year}%. ${fundA.sr_five_year > fundB.sr_five_year ? fundAName : fundBName} is better for long-term growth.`,
    });
  }

  faqData.push(
    {
      question: `What is the risk comparison between ${fundAName} and ${fundBName}?`,
      answer: `${fundAName} is ${riskComparison} than ${fundBName}. Choose a fund based on your risk tolerance and goals.`,
    },
    {
      question: `${fundAName} vs ${fundBName}: Which has better AUM growth?`,
      answer: `${fundAName} has Rs ${fundA.asset_size_cr} crore in AUM and ${fundBName} has Rs ${fundB.asset_size_cr} crore. A larger, growing AUM shows that investors trust the fund and indicates stability.`,
    },
    {
      question: `Which fund has a lower expense ratio, ${fundAName} or ${fundBName}?`,
      answer: `${fundAName} has an expense ratio of ${fundA.expense_ratio}% and ${fundBName} has ${fundB.expense_ratio}%. ${lowerExpenseFund.scheme_name} has a lower ratio, which means it can give better net returns.`,
    },
    {
      question: `${fundAName} or ${fundBName}: Which beats its benchmark more consistently?`,
      answer: `${fundAName} has outperformed the benchmark more consistently with an average of ${fundABenchmark5Y}% over 5 years than ${fundBName} with ${fundBBenchmark5Y}%. ${betterBenchmarkFund.scheme_name} has performed better against its benchmark.`,
    },
  );

  return faqData;
}

// -------- Pros and Cons of Funds ----------
export async function fetchProsConsForFunds(fundsDataBySlug: OverlapSchemes[]): Promise<FundProsConsResult[]> {
  if (fundsDataBySlug.length === 0) return [];

  const results = await Promise.all(
    fundsDataBySlug.map(async (fund) => {
      const payload: SchemeProsConsPayload = {
        scheme_code: fund.scheme_code,
        scheme_category_id: Number(fund.scheme_category_id),
        kra: fund.kra,
      };

      const data = await fetchSchemeProsCons(payload);

      return {
        fundId: fund.id,
        scheme_code: fund.scheme_code,
        pros: data.pros,
        cons: data.cons,
      };
    }),
  );

  return results;
} 

export function updateSeriesVisibility(chart: Highcharts.Chart): void {
  if (!chart.xAxis || chart.xAxis.length <= 0) return;

  const xAxis = chart.xAxis[0];
  const currentMin: number = xAxis.min ?? 0;

  // if (typeof currentMin === "number") {
  //   const d = new Date(currentMin);
  //   currentMin = Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate());
  // }

  const makeVisible: number[] = [];
  let index = 0;

  const d = new Date(currentMin);
  d.setMonth(d.getMonth() + 1);

  // for (const series of chart.series) {
  //   const s = series as Highcharts.Series;

  //   s.setVisible(false, false);

  //   const rawData = s.options?.data;
  //   const firstPoint = rawData[0];

  //   if (firstPoint[0] <= d.getTime()) {
  //     makeVisible.push(index);
  //   }
  //   firstPoint[0];
  //   index++;
  // }

  for (const s of chart.series) {
    s.setVisible(false, false);

    const options = s.options;

    if ("data" in options && Array.isArray(options.data) && options.data.length > 0) {
      const firstPoint = options.data[0] as Highcharts.PointOptionsType;

      if (Array.isArray(firstPoint)) {
        const x = firstPoint[0] as number;

        if (x <= d.getTime()) {
          makeVisible.push(index);
        }
      }
    }

    index++;
  }

  for (const element of makeVisible) {
    chart.series[element].setVisible(true, false);
  }

  chart.redraw();
}
